import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Calendar as CalendarIcon, Clock, Copy, Download, Plus, RotateCcw,
  Save, Trash2, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DAYS = [
  { key: 'monday',    label: 'Montag' },
  { key: 'tuesday',   label: 'Dienstag' },
  { key: 'wednesday', label: 'Mittwoch' },
  { key: 'thursday',  label: 'Donnerstag' },
  { key: 'friday',    label: 'Freitag' },
  { key: 'saturday',  label: 'Samstag' },
  { key: 'sunday',    label: 'Sonntag' },
];

const CANTONS = [
  { code: 'ZH', name: 'Zürich' },
  { code: 'SG', name: 'St. Gallen' },
];

const TYPE_COLORS = {
  federal:      'bg-rose-100 text-rose-700',
  cantonal_zh:  'bg-amber-100 text-amber-700',
  cantonal_sg:  'bg-amber-100 text-amber-700',
  custom:       'bg-slate-100 text-slate-700',
};

const StudioHoursPage = () => {
  const [studios, setStudios] = useState([]);
  const [selectedStudioId, setSelectedStudioId] = useState('');
  const [week, setWeek] = useState(null);
  const [hasCustom, setHasCustom] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySrc, setCopySrc] = useState('');
  const [addHolidayOpen, setAddHolidayOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', type: 'custom' });

  const token = () => localStorage.getItem('token');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  // ---------- Initial load ----------
  useEffect(() => { loadStudios(); }, []);
  useEffect(() => {
    if (selectedStudioId) {
      loadWeek();
      loadHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudioId, year]);

  const loadStudios = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/studios/all`, auth());
      const list = res.data || [];
      setStudios(list);
      if (!selectedStudioId && list.length > 0) {
        const def = list.find((s) => s.is_default) || list[0];
        setSelectedStudioId(def.id);
      }
    } catch (e) {
      toast.error('Studios konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadWeek = async () => {
    try {
      const res = await axios.get(`${API}/studios/${selectedStudioId}/availability`, auth());
      const data = res.data;
      setHasCustom(!!data.has_custom);
      const w = {};
      DAYS.forEach((d) => {
        const dd = data[d.key] || {};
        w[d.key] = {
          active: !!dd.active,
          from: dd.slots?.[0]?.from || '09:00',
          to:   dd.slots?.[0]?.to   || '18:00',
        };
      });
      setWeek(w);
    } catch (e) {
      toast.error('Öffnungszeiten konnten nicht geladen werden');
    }
  };

  const loadHolidays = async () => {
    try {
      const res = await axios.get(`${API}/studios/${selectedStudioId}/holidays`, {
        ...auth(),
        params: { year },
      });
      setHolidays(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // ---------- Save ----------
  const handleSave = async () => {
    if (!week) return;
    setSaving(true);
    try {
      const payload = {};
      DAYS.forEach((d) => {
        payload[d.key] = {
          active: !!week[d.key].active,
          slots: [{ from: week[d.key].from, to: week[d.key].to }],
        };
      });
      await axios.put(
        `${API}/studios/${selectedStudioId}/availability`,
        payload,
        auth(),
      );
      toast.success('Öffnungszeiten gespeichert');
      setHasCustom(true);
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Studio-eigene Öffnungszeiten löschen? Dann gilt der globale Default.')) return;
    try {
      await axios.delete(`${API}/studios/${selectedStudioId}/availability`, auth());
      toast.success('Auf Default zurückgesetzt');
      loadWeek();
    } catch (e) {
      toast.error('Reset fehlgeschlagen');
    }
  };

  // ---------- Copy ----------
  const otherStudios = useMemo(
    () => studios.filter((s) => s.id !== selectedStudioId),
    [studios, selectedStudioId],
  );

  const handleCopy = async () => {
    if (!copySrc) return;
    try {
      await axios.post(
        `${API}/studios/${selectedStudioId}/availability/copy-from/${copySrc}`,
        {},
        auth(),
      );
      toast.success('Öffnungszeiten kopiert');
      setCopyOpen(false);
      setCopySrc('');
      loadWeek();
    } catch (e) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  // ---------- Holidays ----------
  const handleImportCanton = async (canton) => {
    try {
      const res = await axios.post(
        `${API}/studios/${selectedStudioId}/holidays/import-cantonal`,
        null,
        { ...auth(), params: { year, canton } },
      );
      toast.success(`${res.data.imported} Feiertage importiert (${res.data.skipped} Duplikate übersprungen)`);
      loadHolidays();
    } catch (e) {
      toast.error('Import fehlgeschlagen');
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Name + Datum erforderlich');
      return;
    }
    try {
      await axios.post(
        `${API}/studios/${selectedStudioId}/holidays`,
        newHoliday,
        auth(),
      );
      toast.success('Feiertag hinzugefügt');
      setAddHolidayOpen(false);
      setNewHoliday({ name: '', date: '', type: 'custom' });
      loadHolidays();
    } catch (e) {
      toast.error('Hinzufügen fehlgeschlagen');
    }
  };

  const handleDeleteHoliday = async (h) => {
    if (!h.studio_id) {
      toast.error('Globale Feiertage können hier nicht gelöscht werden. Bitte unter Admin → Feiertage löschen.');
      return;
    }
    if (!window.confirm(`Feiertag "${h.name}" am ${h.date} löschen?`)) return;
    try {
      await axios.delete(`${API}/studios/${selectedStudioId}/holidays/${h.id}`, auth());
      toast.success('Feiertag gelöscht');
      loadHolidays();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  if (loading || !week) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  const selectedStudio = studios.find((s) => s.id === selectedStudioId);

  return (
    <div className="p-4 md:p-8 max-w-6xl space-y-6" data-testid="studio-hours-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="h-6 w-6 text-teal-600" />
            Studio-Öffnungszeiten &amp; Feiertage
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Individuelle Wochenplanung &amp; Feiertage pro Studio
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedStudioId} onValueChange={setSelectedStudioId}>
            <SelectTrigger className="w-[220px]" data-testid="studio-hours-picker">
              <SelectValue placeholder="Studio wählen" />
            </SelectTrigger>
            <SelectContent>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`studio-hours-option-${s.id}`}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> {s.name}
                    {s.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* WEEK PLAN CARD */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              Wochenplan: {selectedStudio?.name}
              {hasCustom ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 text-[10px]">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Eigene Zeiten gepflegt
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">
                  Default (kein Custom)
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCopyOpen(true)}
                disabled={otherStudios.length === 0}
                data-testid="studio-hours-copy"
              >
                <Copy className="h-4 w-4 mr-1" />
                Aus anderem Studio kopieren
              </Button>
              {hasCustom && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetToDefault}
                  data-testid="studio-hours-reset"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Auf Default zurücksetzen
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {DAYS.map((d) => (
            <div
              key={d.key}
              className="flex items-center gap-3 p-2.5 rounded-md border border-slate-100 hover:border-teal-200 transition"
              data-testid={`day-row-${d.key}`}
            >
              <div className="w-24 text-sm font-medium text-gray-700">{d.label}</div>
              <Switch
                checked={week[d.key].active}
                onCheckedChange={(v) => setWeek({ ...week, [d.key]: { ...week[d.key], active: !!v } })}
                data-testid={`day-active-${d.key}`}
              />
              {week[d.key].active ? (
                <div className="flex items-center gap-2 ml-2">
                  <Input
                    type="time"
                    value={week[d.key].from}
                    onChange={(e) => setWeek({ ...week, [d.key]: { ...week[d.key], from: e.target.value } })}
                    className="w-28"
                    data-testid={`day-from-${d.key}`}
                  />
                  <span className="text-gray-400">—</span>
                  <Input
                    type="time"
                    value={week[d.key].to}
                    onChange={(e) => setWeek({ ...week, [d.key]: { ...week[d.key], to: e.target.value } })}
                    className="w-28"
                    data-testid={`day-to-${d.key}`}
                  />
                </div>
              ) : (
                <span className="text-sm text-gray-400 italic ml-2">geschlossen</span>
              )}
            </div>
          ))}
          <div className="pt-3 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="studio-hours-save"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichert...' : 'Wochenplan speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* HOLIDAYS CARD */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-rose-600" />
              Feiertage {year} ({selectedStudio?.short_name || selectedStudio?.name})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]" data-testid="holiday-year-picker">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[year - 1, year, year + 1].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={(v) => handleImportCanton(v)}>
                <SelectTrigger className="w-[200px]" data-testid="holiday-import-picker">
                  <SelectValue placeholder="Kanton importieren..." />
                </SelectTrigger>
                <SelectContent>
                  {CANTONS.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`holiday-import-${c.code}`}>
                      <span className="flex items-center gap-2">
                        <Download className="h-3 w-3" /> Bundes + {c.name} ({c.code})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddHolidayOpen(true)}
                data-testid="holiday-add-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Eigener Feiertag
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Noch keine Feiertage für {year}</p>
              <p className="text-xs mt-1">Importiere Schweizer Feiertage über den Kanton-Picker oder füge eigene hinzu.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {holidays.map((h) => (
                <div
                  key={`${h.id}-${h.date}`}
                  className="flex items-center justify-between p-2.5 rounded-md border border-slate-100 hover:border-rose-200 transition"
                  data-testid={`holiday-row-${h.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-xs font-mono text-gray-500 w-24">{h.date}</div>
                    <div className="text-sm font-medium text-gray-800">{h.name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] border-0 ${TYPE_COLORS[h.type] || TYPE_COLORS.custom}`}>
                      {h.type === 'federal' ? 'Bund'
                       : h.type === 'cantonal_zh' ? 'Kanton ZH'
                       : h.type === 'cantonal_sg' ? 'Kanton SG'
                       : 'Eigen'}
                    </Badge>
                    {!h.studio_id ? (
                      <Badge variant="outline" className="text-[10px]">global</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteHoliday(h)}
                        className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700"
                        data-testid={`holiday-delete-${h.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 text-[11px] text-gray-500 italic flex items-start gap-1">
            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              An Feiertagen werden im Online-Buchungs-Kalender keine Slots
              angeboten. Kunden erhalten zusätzlich automatische Erinnerungen
              (sofern in Admin → Feiertage aktiviert).
            </span>
          </div>
        </CardContent>
      </Card>

      {/* COPY DIALOG */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wochenplan kopieren</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-sm text-gray-600">
              Kopiert den Wochenplan eines anderen Studios komplett auf
              <span className="font-semibold"> {selectedStudio?.name}</span>.
              Vorhandene Zeiten werden überschrieben.
            </p>
            <Select value={copySrc} onValueChange={setCopySrc}>
              <SelectTrigger data-testid="copy-source-picker">
                <SelectValue placeholder="Quell-Studio wählen" />
              </SelectTrigger>
              <SelectContent>
                {otherStudios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Abbrechen</Button>
            <Button
              onClick={handleCopy}
              disabled={!copySrc}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="copy-source-submit"
            >
              <Copy className="h-4 w-4 mr-2" />
              Kopieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD HOLIDAY DIALOG */}
      <Dialog open={addHolidayOpen} onOpenChange={setAddHolidayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eigenen Feiertag hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                placeholder='z.B. "Betriebsferien"'
                data-testid="new-holiday-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Datum</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                data-testid="new-holiday-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddHolidayOpen(false)}>Abbrechen</Button>
            <Button
              onClick={handleAddHoliday}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="new-holiday-submit"
            >
              <Plus className="h-4 w-4 mr-2" />
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudioHoursPage;
